import { marked } from 'marked'
import mjml2html from 'mjml-browser'

/**
 * Generates HTML for social media links
 * @param {Array} links - Array of social media link objects with platform and url properties
 * @returns {string} HTML string for social media links
 */

export function generateSocialMediaLinksHtml(links) {
  return links
    .filter(link => link.url && link.platform)
    .map(
      link => `
    <a href="${link.url}" style="text-decoration: none; margin: 0 5px;">
      <img 
        src="https://dashboard.directful.com/social-icons/${link.platform.toLowerCase()}.png" 
        alt="${link.platform}" 
        style="width: 25px; height: 25px; opacity: 0.4; display: inline-block;"
      />
    </a>
  `
    )
    .join('\n')
}

/**
 * Converts MJML to HTML with variable substitution
 * @param {string} mjml - MJML template string
 * @param {Object} variables - Variables to substitute in the template
 * @param {Object} html - Optional HTML content object with keys and corresponding HTML
 * @returns {string} Generated HTML string
 */
export const getHtmlFromMjml = (mjml, variables, html = {}) => {
  let populatedMjml = mjml
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g')
    populatedMjml = populatedMjml.replace(regex, value || '')
  })
  const { html: generatedHtml, errors } = mjml2html(populatedMjml, {
    validationLevel: 'soft',
    keepComments: true
  })

  if (errors && errors.length > 0) {
    console.error('MJML Errors:', errors)
    alert('There were errors generating the email HTML. Check console for details.')
    return ''
  }

  // Replace HTML placeholders with provided content
  let finalHtml = generatedHtml

  Object.entries(html).forEach(([key, content]) => {
    const placeholder = `<!-- ${key} -->`
    finalHtml = finalHtml.replace(placeholder, content || '')
  })

  return finalHtml
}

/**
 * Replaces token syntax with template variable syntax
 * @param {string} bodyContent - Content with token syntax
 * @returns {string} Content with template variable syntax
 */
export function replaceTagsSyntax(bodyContent) {
  return bodyContent.replace(/:token\{key="(.*?)"\s+label="(.*?)"\}/g, (match, p1) => {
    if (p1 === 'guestName') {
      return '{{firstName}}'
    }

    if (p1 === 'guestLastName') {
      return '{{lastName}}'
    }

    if (p1 === 'checkoutDate') {
      return '{{checkOut}}'
    }
  })
}

/**
 * Replaces template variables with preview content
 * @param {string} bodyContent - Content with template variables
 * @param {string} userName - User name for preview
 * @returns {string} Content with preview values
 */
export function replaceTagsWithPreviewContent(bodyContent, userName) {
  return bodyContent.replace(/{{(.*?)}}/g, (match, p1) => {
    if (p1 === 'firstName') {
      return userName?.split(' ')[0] || 'Guest'
    }

    if (p1 === 'lastName') {
      return userName?.split(' ')[1] || ''
    }

    if (p1 === 'checkOut') {
      return 'December 29, 2024'
    }

    return match
  })
}

/**
 * Generates HTML for email with all processing
 * @param {Object} params - Parameters for email generation
 * @param {string} params.mjmlTemplate - MJML template string
 * @param {Object} params.variables - Variables to substitute in the template
 * @param {string} params.body - Email body content
 * @param {Array} params.socialMediaLinks - Social media links array
 * @returns {string} Generated HTML
 */
export function generateEmailHtml({ mjmlTemplate, variables, body, socialMediaLinks }) {
  return getHtmlFromMjml(mjmlTemplate, variables, {
    bodyHtml: marked.parse(replaceTagsSyntax(body)),
    socialMediaLinksHtml: generateSocialMediaLinksHtml(socialMediaLinks)
  })
}

/**
 * Extracts social media URLs from links array
 * @param {Array} socialMediaLinks - Array of social media link objects
 * @returns {Object} Object with platform-specific URLs
 */
export function extractSocialMediaUrls(socialMediaLinks) {
  return {
    facebookUrl:
      socialMediaLinks.find(link => link.platform.toLowerCase() === 'facebook')?.url ||
      '',
    twitterUrl:
      socialMediaLinks.find(link => link.platform.toLowerCase() === 'twitter')?.url || '',
    instagramUrl:
      socialMediaLinks.find(link => link.platform.toLowerCase() === 'instagram')?.url ||
      '',
    linkedinUrl:
      socialMediaLinks.find(link => link.platform.toLowerCase() === 'linkedin')?.url || ''
  }
}

/**
 * Creates a state updater function for a specific property
 * @param {Function} setEmailState - The state setter function
 * @param {string} property - The property name to update
 * @returns {Function} A function that updates the specific property
 */
export function createEmailStateUpdater(setEmailState, property) {
  return value => {
    setEmailState(prevState => ({
      ...prevState,
      [property]: value
    }))
  }
}

/**
 * Creates multiple state updater functions for email properties
 * @param {Function} setEmailState - The state setter function
 * @param {Array} properties - Array of property names
 * @returns {Object} Object with updater functions for each property
 */
export function createEmailStateUpdaters(setEmailState, properties) {
  const updaters = {}
  properties.forEach(property => {
    updaters[`set${property.charAt(0).toUpperCase() + property.slice(1)}`] =
      createEmailStateUpdater(setEmailState, property)
  })
  return updaters
}
